import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import SelectInput from "./SelectInput";
import Spinner from "./Spinner";
import { server2 } from "../constant/config";
import {
  getBoards,
  getChapters,
  getClasses,
  getSubjectMapping,
  getSubjects,
} from "../constant/urls";
import { useAuthConfig } from "../hooks/useAuthConfig";

// Custom hook for data fetching
const useCurriculumData = (initialSelections) => {
  const axiosConfig = useAuthConfig();
  const [data, setData] = useState({
    boards: {},
    subjects: {},
    classes: {},
    subjectMapping: [],
    chapters: [],
    topics: [],
    nestedData: {},
    loading: {
      initial: true,
      topics: false,
    },
    error: null,
  });

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [boardsRes, subjectsRes, classesRes, subjectMapRes, chaptersRes] =
          await Promise.all([
            axios.get(`${server2}${getBoards}`, axiosConfig),
            axios.get(`${server2}${getSubjects}`, axiosConfig),
            axios.get(`${server2}${getClasses}`, axiosConfig),
            axios.get(`${server2}${getSubjectMapping}`, axiosConfig),
            axios.get(`${server2}${getChapters}`, axiosConfig),
          ]);

        const mapById = (arr, keyField, labelField) =>
          arr.reduce((acc, item) => {
            acc[item[keyField]] = item[labelField];
            return acc;
          }, {});

        setData((prev) => ({
          ...prev,
          boards: mapById(boardsRes.data.boards || [], "id", "short_name"),
          subjects: mapById(subjectsRes.data.subjects || [], "id", "name"),
          classes: mapById(classesRes.data.classes || [], "id", "name"),
          subjectMapping: subjectMapRes.data.subjectsmap || [],
          chapters: chaptersRes.data.details?.chapters || [],
          loading: { ...prev.loading, initial: false },
        }));
      } catch (err) {
        console.error("Error fetching curriculum data:", err);
        setData((prev) => ({
          ...prev,
          error: "Failed to load curriculum data. Please try again later.",
          loading: { ...prev.loading, initial: false },
        }));
      }
    };

    fetchInitialData();
  }, []);

  // Fetch topics by chapter
  const fetchTopics = useCallback(async (chapterId) => {
    if (!chapterId) {
      setData((prev) => ({ ...prev, topics: [] }));
      return;
    }

    try {
      setData((prev) => ({
        ...prev,
        loading: { ...prev.loading, topics: true },
        error: null,
      }));

      const response = await axios.get(
        `${server2}/v1/topicsbychapter?chapterid=${chapterId}`,
        axiosConfig
      );

      setData((prev) => ({
        ...prev,
        topics: response.data.content || [],
        loading: { ...prev.loading, topics: false },
      }));
    } catch (error) {
      console.error("Error fetching topics:", error);
      setData((prev) => ({
        ...prev,
        error: "Failed to load topics for selected chapter",
        loading: { ...prev.loading, topics: false },
      }));
    }
  }, []);

  // Build nested structure
  useEffect(() => {
    if (
      Object.keys(data.boards)?.length &&
      Object.keys(data.subjects)?.length &&
      Object.keys(data.classes)?.length &&
      data.subjectMapping?.length &&
      data.chapters?.length
    ) {
      const nested = {};

      const enrichedChapters = data.chapters.map((chapter) => ({ ...chapter }));

      data.subjectMapping.forEach(({ board_id, class_id, subject_id }) => {
        if (!nested[board_id]) {
          nested[board_id] = { name: data.boards[board_id], classes: {} };
        }
        if (!nested[board_id].classes[class_id]) {
          nested[board_id].classes[class_id] = {
            name: data.classes[class_id],
            subjects: {},
          };
        }
        if (!nested[board_id].classes[class_id].subjects[subject_id]) {
          nested[board_id].classes[class_id].subjects[subject_id] = {
            name: data.subjects[subject_id],
            chapters: [],
          };
        }
      });

      enrichedChapters.forEach((chapter) => {
        const { board_id, class_id, subject_id } = chapter;
        const subjectSlot =
          nested[board_id]?.classes?.[class_id]?.subjects?.[subject_id];
        if (subjectSlot) subjectSlot.chapters.push(chapter);
      });

      setData((prev) => ({ ...prev, nestedData: nested }));
    }
  }, [
    data.boards,
    data.classes,
    data.subjects,
    data.subjectMapping,
    data.chapters,
  ]);

  return { data, fetchTopics };
};

const CurriculumSelector = ({
  initialSelections = {},
  onChange = () => {},
  className = "",
}) => {
  const [selections, setSelections] = useState({
    board: initialSelections.board || "",
    class: initialSelections.class || "",
    subject: initialSelections.subject || "",
    chapter: initialSelections.chapter || "",
    topic: initialSelections.topic || "",
  });

  const { data, fetchTopics } = useCurriculumData(initialSelections);

  // Inform parent about selection changes
  useEffect(() => {
    onChange(selections);
  }, [selections, onChange]);

  // Fetch topics when chapter changes
  useEffect(() => {
    fetchTopics(selections.chapter);
  }, [selections.chapter, fetchTopics]);

  const handleSelectionChange = useCallback((type, value) => {
    setSelections((prev) => {
      const next = { ...prev, [type]: value };
      switch (type) {
        case "board":
          return { ...next, class: "", subject: "", chapter: "", topic: "" };
        case "class":
          return { ...next, subject: "", chapter: "", topic: "" };
        case "subject":
          return { ...next, chapter: "", topic: "" };
        case "chapter":
          return { ...next, topic: "" };
        default:
          return next;
      }
    });
  }, []);

  // Options derived from selections and data
  const {
    classOptions,
    subjectOptions,
    chapterOptions,
    topicOptions,
    boardOptions,
  } = useMemo(() => {
    const currentBoard = selections.board;
    const currentClass = selections.class;
    const currentSubject = selections.subject;

    const boardOptions = Object.entries(data.nestedData).map(
      ([id, { name }]) => ({
        value: id,
        label: name,
      })
    );

    const classOptions =
      currentBoard && data.nestedData[currentBoard]
        ? Object.entries(data.nestedData[currentBoard].classes).map(
            ([id, { name }]) => ({ value: id, label: name })
          )
        : [];

    const subjectOptions =
      currentBoard &&
      currentClass &&
      data.nestedData[currentBoard]?.classes[currentClass]
        ? Object.entries(
            data.nestedData[currentBoard].classes[currentClass].subjects
          ).map(([id, { name }]) => ({ value: id, label: name }))
        : [];

    const chapterOptions =
      currentBoard &&
      currentClass &&
      currentSubject &&
      data.nestedData[currentBoard]?.classes[currentClass]?.subjects[
        currentSubject
      ]
        ? data.nestedData[currentBoard].classes[currentClass].subjects[
            currentSubject
          ].chapters.map((ch) => ({ value: ch.id, label: ch.name }))
        : [];

    // Robust handling for topics shape (can be array or object)
    const topicOptions = (() => {
      const topics = data.topics;
      if (Array.isArray(topics)) {
        return topics.map((t) => ({
          value: t?.id ?? t?.topicId ?? String(t),
          label: t?.name ?? String(t),
        }));
      }
      if (topics && typeof topics === "object") {
        return Object.entries(topics).map(([id, obj]) => ({
          value: id,
          label: obj?.name ?? String(obj),
        }));
      }
      return [];
    })();

    return {
      classOptions,
      subjectOptions,
      chapterOptions,
      topicOptions,
      boardOptions,
    };
  }, [selections, data.nestedData, data.topics]);

  return (
    <div
      className={`rounded-xl border border-gray-200 bg-white/80 shadow-sm p-4 md:p-5 space-y-4 ${className}`}
    >
      {data.loading.initial && (
        <div className="p-3 bg-blue-50 text-blue-700 rounded-lg border border-blue-200 flex items-center gap-2 text-sm">
          <Spinner size="sm" />
          <span>Preparing curriculum options...</span>
        </div>
      )}

      {data.error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg border border-red-200 flex items-center justify-between text-sm">
          <span>{data.error}</span>
          <button
            className="ml-3 px-3 py-1 rounded-md bg-red-100 text-red-800 hover:bg-red-200 transition"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="relative">
          <SelectInput
            label="Board"
            value={selections.board}
            onChange={(e) => handleSelectionChange("board", e.target.value)}
            options={boardOptions}
            disabled={data.loading.initial}
            placeholder={
              data.loading.initial ? "Loading boards..." : "-- Select Board --"
            }
          />
          {data.loading.initial && (
            <div className="absolute right-3 top-9">
              <Spinner size="sm" />
            </div>
          )}
        </div>

        <div className="relative">
          <SelectInput
            label="Class"
            value={selections.class}
            onChange={(e) => handleSelectionChange("class", e.target.value)}
            disabled={!selections.board || data.loading.initial}
            options={classOptions}
            placeholder={
              data.loading.initial
                ? "Loading classes..."
                : !selections.board
                ? "Select board first"
                : "-- Select Class --"
            }
          />
          {data.loading.initial && (
            <div className="absolute right-3 top-9">
              <Spinner size="sm" />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="relative">
          <SelectInput
            label="Subject"
            value={selections.subject}
            onChange={(e) => handleSelectionChange("subject", e.target.value)}
            disabled={!selections.class || data.loading.initial}
            options={subjectOptions}
            placeholder={
              data.loading.initial
                ? "Loading subjects..."
                : !selections.class
                ? "Select class first"
                : "-- Select Subject --"
            }
          />
          {data.loading.initial && (
            <div className="absolute right-3 top-9">
              <Spinner size="sm" />
            </div>
          )}
        </div>

        <div className="relative">
          {initialSelections.chapter && (
            <SelectInput
              label="Chapter"
              value={selections.chapter}
              onChange={(e) => handleSelectionChange("chapter", e.target.value)}
              disabled={!selections.subject || data.loading.initial}
              options={chapterOptions}
              placeholder={
                data.loading.initial
                  ? "Loading chapters..."
                  : !selections.subject
                  ? "Select subject first"
                  : "-- Select Chapter --"
              }
            />
          )}
          {(data.loading.initial || data.loading.topics) && (
            <div className="absolute right-3 top-9">
              <Spinner size="sm" />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="relative">
          {initialSelections.topic && (
            <SelectInput
              label="Topic"
              value={selections.topic}
              onChange={(e) => handleSelectionChange("topic", e.target.value)}
              disabled={
                !selections.chapter || data.loading.initial || data.loading.topics
              }
              options={topicOptions}
              placeholder={
                data.loading.initial
                  ? "Loading topics..."
                  : data.loading.topics
                  ? "Loading topics..."
                  : !selections.chapter
                  ? "Select chapter first"
                  : "-- Select Topic --"
              }
            />
          )}
          {(data.loading.initial || data.loading.topics) && (
            <div className="absolute right-3 top-9">
              <Spinner size="sm" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CurriculumSelector;
